import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { shows } from '@/mocks/shows';
import { filterShows, paginate, sortShows, type SortKey, type FilterOptions } from '@/utils/shows';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import ShowCard from '@/components/feature/ShowCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterPanel, { type RangeOption } from '@/components/feature/FilterPanel';
import EmptyState from '@/components/feature/EmptyState';

const PAGE_SIZE = 12;

const scoreOptions: RangeOption[] = [
  { label: '不限' },
  { label: '9.0 分以上', min: 9 },
  { label: '8.5 分以上', min: 8.5 },
  { label: '8.0 分以上', min: 8 },
];

const yearOptions: RangeOption[] = [
  { label: '不限' },
  { label: '2024-2026', min: 2024, max: 2026 },
  { label: '2020-2023', min: 2020, max: 2023 },
  { label: '2018-2019', min: 2018, max: 2019 },
];

const sortOptions: { label: string; key: SortKey }[] = [
  { label: '按评分', key: 'score' },
  { label: '按更新时间', key: 'updated' },
  { label: '按年份', key: 'year' },
];

export default function CategoryPage() {
  const { type = '' } = useParams();
  const category = categories.find((item) => item.slug === type);

  const [scoreIndex, setScoreIndex] = useState(0);
  const [yearIndex, setYearIndex] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useDocumentMeta({
    title: category
      ? `${category.name}高评分电视剧推荐 - 高评分电视剧大全`
      : '分类未找到 - 高评分电视剧',
    description: category
      ? `${category.desc} 收录${category.name}类别下的高评分电视剧，支持按评分、年份与标签筛选。`
      : '未找到对应的剧集分类，请返回首页浏览高评分电视剧。',
  });

  const baseList = useMemo(
    () => (category ? shows.filter((show) => show.categories.includes(category.slug)) : []),
    [category],
  );

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((show) => show.tags.forEach((item) => set.add(item)));
    return Array.from(set);
  }, [baseList]);

  const filtered = useMemo(() => {
    if (!category) return [];
    const options: FilterOptions = {
      minScore: scoreOptions[scoreIndex].min,
      maxScore: scoreOptions[scoreIndex].max,
      minYear: yearOptions[yearIndex].min,
      maxYear: yearOptions[yearIndex].max,
      tag,
    };
    return sortShows(filterShows(baseList, options), sortKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseList, category, scoreIndex, yearIndex, sortKey, tag]);

  const visible = paginate(filtered, page, PAGE_SIZE);
  const hasMore = filtered.length > visible.length;

  const resetFilters = () => {
    setScoreIndex(0);
    setYearIndex(0);
    setSortKey('score');
    setTag('');
    setPage(1);
  };

  const changeScore = (index: number) => {
    setScoreIndex(index);
    setPage(1);
  };
  const changeYear = (index: number) => {
    setYearIndex(index);
    setPage(1);
  };
  const changeSort = (key: SortKey) => {
    setSortKey(key);
    setPage(1);
  };
  const changeTag = (value: string) => {
    setTag(value);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="px-4 md:px-6 py-16 md:py-24">
        <EmptyState
          icon="ri-folder-unknow-line"
          title="未找到该分类"
          description="你访问的分类不存在或已调整，可以返回首页浏览全部高评分剧集。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: '分类', to: '/category/guochan' }, { label: category.name }]} />

      <header className="mt-5 md:mt-7 max-w-3xl">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {category.name}
          </h1>
        </div>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">{category.desc}</p>
        <p className="mt-2 text-sm text-foreground-500">
          共收录 <strong className="text-accent-600">{baseList.length}</strong> 部高评分剧集
        </p>
      </header>

      <div className="mt-6 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileFilterOpen((prev) => !prev)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line"></i>
          筛选与排序
        </button>
        {mobileFilterOpen && (
          <div className="mt-3 animate-fade-in">
            <FilterPanel
              scoreOptions={scoreOptions}
              yearOptions={yearOptions}
              sortOptions={sortOptions}
              activeScore={scoreIndex}
              activeYear={yearIndex}
              activeSort={sortKey}
              availableTags={availableTags}
              activeTag={tag}
              onScoreChange={changeScore}
              onYearChange={changeYear}
              onSortChange={changeSort}
              onTagChange={changeTag}
              onReset={resetFilters}
            />
          </div>
        )}
      </div>

      <div className="hidden lg:block mt-6">
        <FilterPanel
          scoreOptions={scoreOptions}
          yearOptions={yearOptions}
          sortOptions={sortOptions}
          activeScore={scoreIndex}
          activeYear={yearIndex}
          activeSort={sortKey}
          availableTags={availableTags}
          activeTag={tag}
          onScoreChange={changeScore}
          onYearChange={changeYear}
          onSortChange={changeSort}
          onTagChange={changeTag}
          onReset={resetFilters}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="暂无符合条件的电视剧"
            description="当前筛选条件下没有匹配的剧集，试试放宽评分或年份范围。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5 mt-8">
            {visible.map((show) => (
              <ShowCard key={show.id} show={show} showIntro />
            ))}
          </div>

          {hasMore ? (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-down-line"></i>
                加载更多
              </button>
            </div>
          ) : (
            <p className="text-center text-sm text-foreground-400 mt-10">已经到底啦，共 {filtered.length} 部剧集</p>
          )}
        </>
      )}

      <div className="mt-12 flex flex-wrap gap-2">
        <span className="text-sm text-foreground-500 mr-2">其他分类：</span>
        {categories
          .filter((item) => item.slug !== category.slug)
          .map((item) => (
            <Link
              key={item.slug}
              to={`/category/${item.slug}`}
              className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.name}
            </Link>
          ))}
      </div>
    </div>
  );
}